import React, { use } from 'react';

import { remote } from '@code-expert/prelude';
import { useDeepCompareMemo } from '@code-expert/react';
import { useCachedQuery, useQuery } from '/imports/ui/hooks/useProcedure';
import { usePublication } from '/imports/ui/hooks/usePublication';
import type { MessageCount } from './shared';

const useUserMessageCount = (): MessageCount => {
  const lastChange = usePublication('messages.getLastForUser', { method: 'trigger' });
  const [count] = useCachedQuery('messages_getUserInboxCount', { props: undefined }, [lastChange]);
  return { count, lastChange };
};

const useNotificationCount = (): MessageCount => {
  const lastChange = usePublication('notifications.getForUser', { method: 'trigger' });
  const [count] = useQuery('notification_getInboxCount', { props: undefined }, [lastChange]);
  return { count, lastChange };
};

interface UserInboxContext {
  userMessages: MessageCount;
  userNotifications: MessageCount;
}

const UserInboxContext = React.createContext<UserInboxContext>({
  userMessages: { count: remote.initial, lastChange: undefined as $IntentionalAny },
  userNotifications: { count: remote.initial, lastChange: undefined as $IntentionalAny },
});

/**
 * By using a context for the user message count information, we can ensure that all components
 * have access to continuous subscriptions and can immediately render the current inbox count.
 * This approach minimises the risk of UI glitches due to volatile component sizes.
 */
export function UserInboxContextProvider({ children }: React.PropsWithChildren) {
  const userMessages = useUserMessageCount();
  const userNotifications = useNotificationCount();

  const value = useDeepCompareMemo(
    () => ({ userMessages, userNotifications }),
    [userMessages, userNotifications],
  );

  return <UserInboxContext value={value}>{children}</UserInboxContext>;
}

export const useUserInboxContext = () => use(UserInboxContext);
